import ArrowRight from "@/assets/images/icons/arrow-right-scroll.svg";
import ExternalLink from "@/components/ExternalLink";
import { LevelIndicatorBars } from "@/components/ui/LevelIndicatorBars";
import { BodyText, EyebrowText } from "@/components/ui/Typography";
import { useHasPurchasedLifetimeCourses } from "@/hooks/usePurchasedLifetimeCourses";
import { goHome } from "@/lib/navigation";
import { instrumentDashboardPath } from "@/lib/userHomeUrl";
import React, { useState } from "react";
import { Platform, Pressable, ScrollView, useWindowDimensions, View } from "react-native";
import { XStack, YStack } from "tamagui";

/** Hard cap on dropdown height for tall screens. */
const SCROLL_MAX_HEIGHT = 630;
/** Space reserved for the header + popover offset + a bottom breathing gap. */
const VIEWPORT_RESERVED = 150;

const isAppPath = (url: string) => url.startsWith("/") && !url.startsWith("//");

type ExploreCourseItem = {
  label: string;
  url: string;
  level?: 1 | 2 | 3;
};

type ExploreCategory = {
  id: string;
  label: string;
  url?: string; // direct link for instruments without sub-items
  items: ExploreCourseItem[];
};

const CATEGORIES: ExploreCategory[] = [
  {
    id: "piano",
    label: "Piano",
    items: [
      {
        label: "Jazz Piano Foundations",
        url: "/courses/jazz-piano-foundations",
        level: 1,
      },
      {
        label: "Jazz Piano Expansions",
        url: "/courses/jazz-piano-expansions",
        level: 2,
      },
      {
        label: "Jazz Piano Method",
        url: "/courses/jazz-piano-method",
        level: 3,
      },
      {
        label: "Harmony Games Unlimited",
        url: "/courses/harmony-games-unlimited",
        level: 2,
      },
      { label: "Core Pathway", url: "/courses/skillpath/piano-core" },
    ],
  },
  {
    id: "guitar",
    label: "Guitar",
    url: instrumentDashboardPath("guitar"),
    items: [],
  },
  {
    id: "bass",
    label: "Bass",
    url: instrumentDashboardPath("bass"),
    items: [],
  },
  {
    id: "drums",
    label: "Drums",
    url: instrumentDashboardPath("drums"),
    items: [],
  },
  {
    id: "vocals",
    label: "Vocals",
    url: instrumentDashboardPath("vocals"),
    items: [],
  },
  {
    id: "saxophone",
    label: "Saxophone",
    url: instrumentDashboardPath("saxophone"),
    items: [],
  },
  {
    id: "trumpet",
    label: "Trumpet",
    url: instrumentDashboardPath("trumpet"),
    items: [],
  },
  {
    id: "vibraphone",
    label: "Vibraphone",
    url: instrumentDashboardPath("vibraphone"),
    items: [],
  },
];

type ExploreDropdownProps = {
  inSheet?: boolean;
  inDrawer?: boolean;
  onNavigate?: () => void;
};

export function ExploreDropdown({
  inSheet = false,
  inDrawer = false,
  onNavigate,
}: ExploreDropdownProps) {
  const [expandedId, setExpandedId] = useState<string>("piano");
  const { hasPurchasedLifetimeCourses } = useHasPurchasedLifetimeCourses();
  const { height: windowHeight } = useWindowDimensions();
  const isWeb = Platform.OS === "web";

  // Fit within the viewport on short windows; scroll internally if needed.
  const scrollMaxHeight = Math.min(
    SCROLL_MAX_HEIGHT,
    Math.max(320, windowHeight - VIEWPORT_RESERVED),
  );

  const courseLinkRow = (label: string, url: string) => {
    const rowInner = (
      <XStack
        ai="center"
        px="$3"
        py="$2"
        borderRadius={10}
        bg="#262626"
        hoverStyle={{ bg: "#333333" }}
        cursor="pointer"
        width="100%"
      >
        <BodyText color="#E5E7EB" flex={1}>
          {label}
        </BodyText>
      </XStack>
    );

    // Native "Browse All Courses" is a home action (popToTop), not a plain link.
    if (!isWeb && url === "/courses") {
      return (
        <Pressable
          onPress={() => {
            onNavigate?.();
            goHome();
          }}
        >
          {rowInner}
        </Pressable>
      );
    }
    return (
      <ExternalLink url={url} onPress={onNavigate} deferNavigation={!inDrawer}>
        {rowInner}
      </ExternalLink>
    );
  };

  const footerCourseLinks =
    !inDrawer || hasPurchasedLifetimeCourses ? (
      <YStack mt="$2" width="100%">
        {!inDrawer ? (
          <View
            style={{
              height: 1,
              width: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              marginBottom: 8,
            }}
          />
        ) : null}
        <YStack gap="$1.5" width="100%">
          {!inDrawer ? courseLinkRow("Browse All Courses", "/courses") : null}
          {!inDrawer ? courseLinkRow("Browse by Instructor", "/instructors") : null}
          {hasPurchasedLifetimeCourses
            ? courseLinkRow("Purchased Courses", "/purchased-courses")
            : null}
        </YStack>
      </YStack>
    ) : null;

  const content = (
    <YStack
      paddingTop={inDrawer ? 0 : 20}
      paddingBottom={inDrawer ? 0 : 20}
      paddingHorizontal={16}
      gap="$2"
    >
      {CATEGORIES.map((category) => {
        const isExpanded = expandedId === category.id;
        const hasItems = category.items.length > 0;

        const categoryHeader = (
          <XStack
            ai="center"
            jc="space-between"
            px="$1"
            py="$1"
            cursor="pointer"
            width="100%"
          >
            <EyebrowText
              textTransform="uppercase"
              letterSpacing={1}
              color="#F9FAFB"
              flex={1}
            >
              {category.label}
            </EyebrowText>

            {hasItems ? (
              <View>
                {isExpanded ? (
                  <ArrowRight
                    width={10}
                    height={10}
                    color="#F9FAFB"
                    style={{ transform: [{ rotate: "-90deg" }] }}
                  />
                ) : (
                  <ArrowRight
                    width={10}
                    height={10}
                    color="#F9FAFB"
                    style={{ transform: [{ rotate: "90deg" }] }}
                  />
                )}
              </View>
            ) : (
              <ArrowRight width={10} height={10} color="#F9FAFB" />
            )}
          </XStack>
        );

        return (
          <YStack key={category.id}>
            {hasItems ? (
              <XStack
                pressStyle={{ opacity: 0.8 }}
                onPress={() => {
                  setExpandedId(expandedId === category.id ? "" : category.id);
                }}
              >
                <YStack flex={1}>{categoryHeader}</YStack>
              </XStack>
            ) : (
              <ExternalLink
                url={category.url!}
                onPress={onNavigate}
                deferNavigation={!inDrawer}
              >
                {categoryHeader}
              </ExternalLink>
            )}

            {isExpanded && hasItems && (
              <YStack mt={12} mb={12} gap="$1.5">
                {category.items.map((item) => {
                  const isInternal = isAppPath(item.url);
                  const pianoDrawerTallRow =
                    inDrawer && category.id === "piano";
                  const itemContent = (
                    <XStack
                      ai="center"
                      jc="space-between"
                      px="$3"
                      py={pianoDrawerTallRow ? 0 : "$2"}
                      height={pianoDrawerTallRow ? 40 : undefined}
                      borderRadius={10}
                      bg="#262626"
                      hoverStyle={{ bg: "#333333" }}
                      cursor="pointer"
                      width="100%"
                    >
                      <BodyText color="#E5E7EB" flex={1}>
                        {item.label}
                      </BodyText>
                      {isInternal && item.level != null ? (
                        <LevelIndicatorBars
                          level={item.level}
                          activeColor="#FFFFFF"
                          inactiveColor="rgba(92, 88, 84, 1)"
                          size={14}
                        />
                      ) : null}
                    </XStack>
                  );

                  return (
                    <ExternalLink
                      key={item.label}
                      url={item.url}
                      onPress={onNavigate}
                      deferNavigation={!inDrawer}
                    >
                      {itemContent}
                    </ExternalLink>
                  );
                })}
              </YStack>
            )}
          </YStack>
        );
      })}
      {footerCourseLinks}
    </YStack>
  );

  return (
    <ScrollView
      style={
        inSheet || inDrawer ? { flex: 1 } : { maxHeight: scrollMaxHeight }
      }
      contentContainerStyle={inSheet ? { paddingBottom: 16 } : undefined}
      showsVerticalScrollIndicator={false}
      bounces={false}
    >
      {content}
    </ScrollView>
  );
}
